import React, { useEffect, useState } from 'react';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import dayjs from 'dayjs';
import { Plus, Trash, Edit, ChevronDown, FileText, Eye, Download } from 'lucide-react';
// Removed static signature; will use configured signature from settings
import { fetchLeads, addLead, updateLead, deleteLead, updateLeadStatus, generateTermSheetFromLead, fetchTermSheet, generateTermSheetPDF, updateTermSheet, fetchFacilities, fetchFacility, authenticatedFetch, fetchConfig } from '../utils/api';

// Get API base URL for image loading
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://spatium-invoice.onrender.com';
  }
  return 'http://localhost:5050';
};

const STATUS_OPTIONS = ['Enquiry', 'Term sheet', 'Closed', 'No response', 'Rejected'];

function Leads({ currentUser, onLeadConverted }) {
  const [leads, setLeads] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [showTermSheetModal, setShowTermSheetModal] = useState(false);
  const [selectedTermSheet, setSelectedTermSheet] = useState(null);
  const [termEdits, setTermEdits] = useState([]);
  const [newAdditionalTerm, setNewAdditionalTerm] = useState({ title: '', content: '' });
  const [showSeatSelectionModal, setShowSeatSelectionModal] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [facilitySeats, setFacilitySeats] = useState([]);
  const [facilityLayoutImage, setFacilityLayoutImage] = useState('');
  const [appConfig, setAppConfig] = useState(null);

  const [form, setForm] = useState({
    name: '', phone: '', email: '', companyName: '', registeredName: '',
    rentableSeats: '', rentPerSeat: '', lockInPeriod: '', securityDeposit: '',
    parking: '', escalationPercentage: '',
    rentStartDate: '', noticePeriodMonths: '', handoverDate: '', status: 'Enquiry', facilityId: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [leadsData, facilitiesData, cfg] = await Promise.all([
          fetchLeads(),
          fetchFacilities(),
          fetchConfig()
        ]);
        setLeads(leadsData);
        setFacilities(facilitiesData || []);
        setAppConfig(cfg || null);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filtered = leads.filter(l => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [l.name, l.companyName, l.registeredName, l.email, l.phone].some(v => (v || '').toLowerCase().includes(s));
  });

  // Validate required fields before generating/viewing a term sheet
  const getMissingFields = (lead) => {
    const requiredFieldToLabel = {
      name: 'Contact Name',
      companyName: 'Company Name',
      registeredName: 'Registered Name',
      rentableSeats: 'Rentable Seats',
      rentPerSeat: 'Rent per Seat',
      lockInPeriod: 'Lock-in Period (months)',
      securityDeposit: 'Security Deposit',
      parking: 'Parking',
      escalationPercentage: 'Escalation %',
      rentStartDate: 'Lease Commencement Date',
      noticePeriodMonths: 'Notice Period (months)',
      handoverDate: 'Handover Date'
    };
    const missing = [];
    Object.keys(requiredFieldToLabel).forEach((key) => {
      const value = lead[key];
      const isEmpty = value === undefined || value === null || value === '';
      if (isEmpty) missing.push(requiredFieldToLabel[key]);
    });
    return missing;
  };

  const toISO = (display) => {
    if (!display) return '';
    const m = String(display).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return display;
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  };

  const loadFacilitySeats = async (facilityId) => {
    if (!facilityId) {
      setFacilitySeats([]);
      setFacilityLayoutImage('');
      return;
    }

    try {
      // Fetch facility details using authenticated API
      const facility = await fetchFacility(facilityId);
      console.log('Facility data:', facility);
      setFacilityLayoutImage(facility.layoutImage || '');

      // Fetch facility seats using authenticated API
      const API_URL = process.env.NODE_ENV === 'production' ? 'https://spatium-invoice.onrender.com/api' : 'http://localhost:5050/api';
      const seatsData = await authenticatedFetch(`${API_URL}/facilities/${facilityId}/seats`);
      console.log('Seats data:', seatsData);
      setFacilitySeats(seatsData.seats || []);
    } catch (err) {
      console.error('Failed to load facility seats:', err);
      setFacilitySeats([]);
      setFacilityLayoutImage('');
    }
  };

  const handleOpenSeatSelection = () => {
    if (!form.facilityId) {
      alert('Please select a facility first');
      return;
    }
    loadFacilitySeats(form.facilityId);
    setShowSeatSelectionModal(true);
  };

  const handleSeatClick = (seat) => {
    if (seat.status === 'booked') return; // Can't select booked seats

    const isSelected = selectedSeats.some(s => s.id === seat.id);
    let newSelectedSeats;
    if (isSelected) {
      newSelectedSeats = selectedSeats.filter(s => s.id !== seat.id);
    } else {
      newSelectedSeats = [...selectedSeats, seat];
    }

    setSelectedSeats(newSelectedSeats);

    // Update rentable seats count automatically
    setForm(prev => ({
      ...prev,
      rentableSeats: newSelectedSeats.length.toString()
    }));
  };

  const handleSaveSeatSelection = () => {
    setShowSeatSelectionModal(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };

    // Add selected seat data
    if (selectedSeats.length > 0) {
      payload.selectedSeats = selectedSeats.map(seat => ({
        id: seat.id,
        x: seat.x,
        y: seat.y,
        type: seat.type,
        status: seat.status
      }));
    }

    if (payload.rentStartDate) payload.rentStartDate = toISO(payload.rentStartDate);
    if (payload.handoverDate) payload.handoverDate = toISO(payload.handoverDate);
    const numericKeys = ['rentableSeats', 'rentPerSeat', 'lockInPeriod', 'securityDeposit', 'parking', 'escalationPercentage', 'noticePeriodMonths'];
    numericKeys.forEach(k => { if (payload[k] === '') delete payload[k]; else payload[k] = Number(payload[k]); });
    if (editing) {
      const updated = await updateLead(editing._id || editing.id, payload);
      setLeads(prev => prev.map(l => (String(l._id || l.id) === String(editing._id || editing.id) ? updated : l)));
    } else {
      const created = await addLead(payload);
      setLeads(prev => [created, ...prev]);
    }
    setEditing(null);
    setSelectedSeats([]);
    setForm({ name: '', phone: '', email: '', companyName: '', registeredName: '', rentableSeats: '', rentPerSeat: '', lockInPeriod: '', securityDeposit: '', parking: '', escalationPercentage: '', rentStartDate: '', noticePeriodMonths: '', handoverDate: '', status: 'Enquiry', facilityId: '' });
    setShowForm(false);
  };

  const onEdit = (lead) => {
    setEditing(lead);
    setForm({
      name: lead.name || '', phone: lead.phone || '', email: lead.email || '', companyName: lead.companyName || '', registeredName: lead.registeredName || '',
      rentableSeats: lead.rentableSeats ?? '', rentPerSeat: lead.rentPerSeat ?? '', lockInPeriod: lead.lockInPeriod ?? '', securityDeposit: lead.securityDeposit ?? '',
      parking: lead.parking ?? '', escalationPercentage: lead.escalationPercentage ?? '',
      rentStartDate: lead.rentStartDate ? dayjs(lead.rentStartDate).format('DD/MM/YYYY') : '',
      noticePeriodMonths: lead.noticePeriodMonths ?? '', handoverDate: lead.handoverDate ? dayjs(lead.handoverDate).format('DD/MM/YYYY') : '',
      status: lead.status || 'Enquiry', facilityId: lead.facilityId?._id || lead.facilityId || ''
    });

    // Load previously selected seats if they exist
    if (lead.selectedSeats && lead.selectedSeats.length > 0) {
      setSelectedSeats(lead.selectedSeats);
    } else {
      setSelectedSeats([]);
    }

    setShowForm(true);
  };

  const onDelete = async (lead) => {
    if (!window.confirm('Delete this lead?')) return;
    await deleteLead(lead._id || lead.id);
    setLeads(prev => prev.filter(l => String(l._id || l.id) !== String(lead._id || lead.id)));
  };

  const onChangeStatus = async (lead, status) => {
    try {
      const updated = await updateLeadStatus(lead._id || lead.id, status);
      setLeads(prev => prev.map(l => String(l._id || l.id) === String(lead._id || lead.id) ? updated : l));

      // If status is "Term sheet", the server automatically creates a term sheet from the lead
      if (status === 'Term sheet') {
        alert('Term sheet has been generated successfully!');
        // Refresh leads list if callback is provided
        if (onLeadConverted) {
          await onLeadConverted();
        }
      }
    } catch (e) {
      alert(e.message || 'Failed to update status');
    }
  };

  const onGenerateTermSheet = async (lead) => {
    const missing = getMissingFields(lead);
    if (missing.length > 0) {
      alert(`Please complete the following details before generating a term sheet:\n\n- ${missing.join('\n- ')}`);
      onEdit(lead);
      return;
    }
    if (!window.confirm('Generate term sheet for this lead?')) return;
    try {
      const result = await generateTermSheetFromLead(lead._id || lead.id);
      alert('Term sheet generated successfully!');
      // Refresh leads to show updated term sheet reference
      const updatedLeads = await fetchLeads();
      setLeads(updatedLeads);
    } catch (e) {
      alert(e.message || 'Failed to generate term sheet');
    }
  };

  const onViewTermSheet = async (lead) => {
    const missing = getMissingFields(lead);
    if (missing.length > 0) {
      alert(`This lead is missing required details to view the term sheet:\n\n- ${missing.join('\n- ')}\n\nPlease complete them first.`);
      onEdit(lead);
      return;
    }
    try {
      const termSheet = await fetchTermSheet(lead.termSheetId);
      setSelectedTermSheet(termSheet);
      setTermEdits(Array.isArray(termSheet.additionalTerms) ? termSheet.additionalTerms : []);
      setShowTermSheetModal(true);
    } catch (e) {
      alert(e.message || 'Failed to load term sheet');
    }
  };

  const onDownloadTermSheet = async (termSheetId, companyName = 'lead') => {
    try {
      const blob = await generateTermSheetPDF(termSheetId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `term-sheet-${companyName}-${termSheetId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert(e.message || 'Failed to download term sheet');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Leads</h1>
          <p>Track enquiries and convert to clients</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Input
            placeholder="Search leads"
            style={{
              minWidth: 280,
              borderRadius: 8,
              border: '1px solid #e1e5e9',
              fontSize: 14
            }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {currentUser?.role !== 'viewer' && (
          <button
            className="btn btn-primary"
            onClick={() => { setEditing(null); setShowForm(true); }}
            style={{
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              background: '#6366f1',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#4f46e5';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#6366f1';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <Plus size={16} />
            New Lead
          </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal-right-overlay">
          <div className="modal-right-content modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>{editing ? 'Edit Lead' : 'Add Lead'}</h2>
              <button className="btn" style={{ background: 'none', border: 'none', fontSize: 20 }} onClick={() => setShowForm(false)}>×</button>
            </div>
            <div className="modal-right-body">
              <form id="lead-form" onSubmit={onSubmit}>
                <div className="info-grid">
                  <div className="form-group"><label>Name</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="form-group"><label>Phone</label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="form-group"><label>Email</label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="form-group"><label>Company Name</label><Input value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} /></div>
                  <div className="form-group"><label>Registered Name</label><Input value={form.registeredName} onChange={e => setForm({ ...form, registeredName: e.target.value })} /></div>
                  <div className="form-group"><label>Facility</label><Select style={{ width: '100%' }} value={form.facilityId || undefined} onChange={v => { setForm({ ...form, facilityId: v, rentableSeats: '' }); setSelectedSeats([]); }} placeholder="Select facility" options={facilities.map(f => ({ label: `${f.facilityName} - ${f.building}${f.tower ? `, ${f.tower}` : ''}${f.floor ? `, Floor ${f.floor}` : ''}`, value: f._id }))} showSearch optionFilterProp="label" allowClear /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleOpenSeatSelection}
                      disabled={!form.facilityId}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="9" width="18" height="13" rx="2" />
                        <path d="M3 9l9-7 9 7" />
                      </svg>
                      Select Seats {selectedSeats.length > 0 && `(${selectedSeats.length} selected)`}
                    </button>
                  </div>
                  <div className="form-group"><label>Rentable Seats</label><InputNumber style={{ width: '100%' }} value={form.rentableSeats === '' ? null : Number(form.rentableSeats)} onChange={v => setForm({ ...form, rentableSeats: v ?? '' })} min={0} /></div>
                  <div className="form-group"><label>Rent per Seat</label><InputNumber style={{ width: '100%' }} value={form.rentPerSeat === '' ? null : Number(form.rentPerSeat)} onChange={v => setForm({ ...form, rentPerSeat: v ?? '' })} min={0} step={0.01} /></div>
                  <div className="form-group"><label>Lock-in Period (months)</label><InputNumber style={{ width: '100%' }} value={form.lockInPeriod === '' ? null : Number(form.lockInPeriod)} onChange={v => setForm({ ...form, lockInPeriod: v ?? '' })} min={0} /></div>
                  <div className="form-group"><label>Security Deposit</label><InputNumber style={{ width: '100%' }} value={form.securityDeposit === '' ? null : Number(form.securityDeposit)} onChange={v => setForm({ ...form, securityDeposit: v ?? '' })} min={0} step={0.01} /></div>
                  <div className="form-group"><label>Parking</label><InputNumber style={{ width: '100%' }} value={form.parking === '' ? null : Number(form.parking)} onChange={v => setForm({ ...form, parking: v ?? '' })} min={0} /></div>
                  <div className="form-group"><label>Escalation %</label><InputNumber style={{ width: '100%' }} value={form.escalationPercentage === '' ? null : Number(form.escalationPercentage)} onChange={v => setForm({ ...form, escalationPercentage: v ?? '' })} min={0} max={100} step={0.01} /></div>
                  <div className="form-group"><label>Rent Start Date</label><DatePicker style={{ width: '100%' }} value={form.rentStartDate ? dayjs(form.rentStartDate, 'DD/MM/YYYY') : null} onChange={d => setForm({ ...form, rentStartDate: d ? d.format('DD/MM/YYYY') : '' })} format="DD/MM/YYYY" /></div>
                  <div className="form-group"><label>Notice Period (months)</label><InputNumber style={{ width: '100%' }} value={form.noticePeriodMonths === '' ? null : Number(form.noticePeriodMonths)} onChange={v => setForm({ ...form, noticePeriodMonths: v ?? '' })} min={0} /></div>
                  <div className="form-group"><label>Handover Date</label><DatePicker style={{ width: '100%' }} value={form.handoverDate ? dayjs(form.handoverDate, 'DD/MM/YYYY') : null} onChange={d => setForm({ ...form, handoverDate: d ? d.format('DD/MM/YYYY') : '' })} format="DD/MM/YYYY" /></div>
                  <div className="form-group"><label>Status</label><Select value={form.status} onChange={v => setForm({ ...form, status: v })} options={STATUS_OPTIONS.map(s => ({ label: s, value: s }))} /></div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" form="lead-form" type="submit">{editing ? 'Update Lead' : 'Add Lead'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Seat Selection Modal */}
      {showSeatSelectionModal && (
        <div
          onClick={() => setShowSeatSelectionModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              maxWidth: '95vw',
              maxHeight: '95vh',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: '#111827' }}>Select Seats</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#6b7280' }}>
                  Click on available seats to select/deselect • {selectedSeats.length} seat(s) selected
                </p>
              </div>
              <button
                onClick={() => setShowSeatSelectionModal(false)}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 8,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span style={{ fontSize: 20, fontWeight: 'bold' }}>×</span>
              </button>
            </div>

            {/* Layout Image with Seats */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              marginBottom: 16,
              border: '2px solid #e5e7eb',
              borderRadius: 8,
              padding: 16,
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'center',
              maxHeight: 'calc(95vh - 200px)'
            }}>
              {facilityLayoutImage ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={`${getApiBaseUrl()}${facilityLayoutImage}`}
                    alt="Facility Layout"
                    draggable={false}
                    style={{
                      width: '1000px',
                      height: '1000px',
                      objectFit: 'cover',
                      borderRadius: 6,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'block'
                    }}
                  />
                  {/* Render seats */}
                  {facilitySeats.map(seat => {
                    const isSelected = selectedSeats.some(s => s.id === seat.id);
                    const isBooked = seat.status === 'booked';

                    let bgColor = '#10b981'; // green - available
                    if (isBooked) bgColor = '#ef4444'; // red - booked
                    if (isSelected) bgColor = '#3b82f6'; // blue - selected

                    return (
                      <div
                        key={seat.id}
                        onClick={() => handleSeatClick(seat)}
                        style={{
                          position: 'absolute',
                          left: seat.x - 12,
                          top: seat.y - 12,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: bgColor,
                          border: isSelected ? '3px solid #1e40af' : '2px solid #fff',
                          cursor: isBooked ? 'not-allowed' : 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 'bold',
                          color: '#fff',
                          transition: 'transform 0.1s, border 0.1s',
                          transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                          zIndex: isSelected ? 10 : 1,
                          opacity: isBooked ? 0.7 : 1
                        }}
                        title={`Seat ${seat.id} - ${seat.status}${isSelected ? ' (Selected)' : ''}`}
                      >
                        {seat.id}
                      </div>
                    );
                  })}
                </div>
              ) : facilitySeats.length > 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ marginBottom: 20 }}>No layout image available for this facility</p>
                  <p style={{ fontSize: 14 }}>This facility has {facilitySeats.length} seat(s) but no visual layout.</p>
                  <p style={{ fontSize: 14, marginTop: 10 }}>
                    To select seats, please upload a layout image in the facility details first.
                  </p>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <p>No layout image or seats available for this facility</p>
                </div>
              )}
            </div>

            {/* Legend */}
            <div style={{
              display: 'flex',
              gap: 20,
              padding: '12px 16px',
              background: '#f9fafb',
              borderRadius: 8,
              marginBottom: 16,
              justifyContent: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontSize: 13, color: '#374151' }}>Available</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#3b82f6' }} />
                <span style={{ fontSize: 13, color: '#374151' }}>Selected</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#ef4444' }} />
                <span style={{ fontSize: 13, color: '#374151' }}>Booked</span>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, borderTop: '2px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
                {selectedSeats.length} seat(s) selected
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSeatSelectionModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSeatSelection}
                >
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-wrapper" style={{
        marginTop: 20,
        borderRadius: 8,
        overflow: 'auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb'
      }}>
        {loading ? (
          <div style={{ padding: 24, color: '#666', textAlign: 'center', fontSize: 16 }}>Loading leads...</div>
        ) : (
          <table className="table" style={{ margin: 0, tableLayout: 'fixed', width: '100%', minWidth: 1100 }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{
                  textAlign: 'left',
                  width: '25%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#111827',
                  borderBottom: 'none'
                }}>Company</th>
                <th style={{
                  textAlign: 'left',
                  width: '20%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#111827',
                  borderBottom: 'none'
                }}>Contact</th>
                <th style={{
                  textAlign: 'left',
                  width: '20%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#111827',
                  borderBottom: 'none'
                }}>Facility</th>
                <th style={{
                  textAlign: 'center',
                  width: '8%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  borderBottom: 'none'
                }}>Seats</th>
                <th style={{
                  textAlign: 'center',
                  width: '10%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  borderBottom: 'none'
                }}>Rent/Seat</th>
                <th style={{
                  textAlign: 'center',
                  width: '12%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  borderBottom: 'none'
                }}>Status</th>
                <th style={{
                  textAlign: 'center',
                  width: '15%',
                  padding: '16px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  borderBottom: 'none'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead, index) => (
                <tr
                  key={lead._id || lead.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  <td style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    verticalAlign: 'top',
                    wordWrap: 'break-word',
                    lineHeight: '1.4',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {lead.registeredName || lead.companyName || '-'}
                  </td>
                  <td style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    verticalAlign: 'top',
                    lineHeight: '1.4'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{lead.name || '-'}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{lead.phone || ''}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{lead.email || ''}</span>
                    </div>
                  </td>
                  <td style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    verticalAlign: 'top',
                    lineHeight: '1.4'
                  }}>
                    {lead.facilityId ? (
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '2px', fontSize: '15px', color: '#111827' }}>{lead.facilityId.facilityName || '-'}</div>
                        <div style={{ color: '#6b7280', fontSize: '12px' }}>
                          {lead.facilityId.building || ''}
                          {lead.facilityId.tower ? `, ${lead.facilityId.tower}` : ''}
                          {lead.facilityId.floor ? `, Floor ${lead.facilityId.floor}` : ''}
                        </div>
                      </div>
                    ) : '-'}
                  </td>
                  <td style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    verticalAlign: 'middle',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {lead.rentableSeats ?? '-'}
                  </td>
                  <td style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    verticalAlign: 'middle',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {lead.rentPerSeat ? `₹${Number(lead.rentPerSeat).toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    verticalAlign: 'middle'
                  }}>
                    {currentUser?.role !== 'viewer' ? (
                      <Select
                        value={lead.status}
                        onChange={(v) => onChangeStatus(lead, v)}
                        options={STATUS_OPTIONS.map(s => ({ label: s, value: s }))}
                        dropdownMatchSelectWidth={false}
                        style={{ minWidth: '100px' }}
                      />
                    ) : (
                      <span style={{ fontSize: 14, color: '#374151' }}>{lead.status}</span>
                    )}
                  </td>
                  <td style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    verticalAlign: 'middle',
                    overflowX: 'auto'
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'nowrap',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      overflowX: 'auto'
                    }}>
                      {currentUser?.role !== 'viewer' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => onEdit(lead)}
                          title="Edit"
                        style={{
                          padding: '8px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          border: '1px solid #d1d5db',
                          background: '#ffffff',
                          color: '#374151',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          minWidth: '32px',
                          height: '32px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <Edit size={14} />
                        </button>
                      )}
                      {currentUser?.role !== 'viewer' && !lead.termSheetId && (
                        <button
                          className="btn btn-primary"
                          onClick={() => onGenerateTermSheet(lead)}
                          title="Generate Term Sheet"
                          style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            minWidth: '32px',
                            height: '32px',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          <FileText size={14} />
                        </button>
                      )}
                      {lead.termSheetId && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => onViewTermSheet(lead)}
                          title="View Term Sheet"
                          style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            border: '1px solid #d1d5db',
                            background: '#ffffff',
                            color: '#374151',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            minWidth: '32px',
                            height: '32px',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                      {currentUser?.role !== 'viewer' && (
                        <button
                          className="btn btn-danger"
                          onClick={() => onDelete(lead)}
                          title="Delete"
                        style={{
                          padding: '8px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          minWidth: '32px',
                          height: '32px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Term Sheet Modal */}
      {showTermSheetModal && selectedTermSheet && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="modal-content" style={{
            background: '#fff',
            borderRadius: '12px',
            padding: 0,
            maxWidth: '900px',
            width: '90vw',
            maxHeight: '90vh',
            position: 'relative',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#ffffff',
              minHeight: '80px'
            }}>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#111827',
                  lineHeight: '1.2'
                }}>
                  Term Sheet
                </h2>
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '16px',
                  color: '#6b7280',
                  fontWeight: '500',
                  lineHeight: '1.4'
                }}>
                  {selectedTermSheet.registeredName || selectedTermSheet.companyName || selectedTermSheet.subLesseeName}
                </p>
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <button
                  className="btn btn-primary"
                  onClick={() => onDownloadTermSheet(selectedTermSheet._id, selectedTermSheet.companyName)}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '44px',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Download size={18} />
                  Download PDF
                </button>
                <button
                  onClick={() => {
                    setShowTermSheetModal(false);
                    setSelectedTermSheet(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    transition: 'all 0.2s ease'
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '32px',
              overflowY: 'auto',
              flex: 1,
              background: '#fff',
              fontFamily: 'Arial, sans-serif'
            }}>
              {/* Header */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>To,</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Spatium Offices</div>
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Date: {new Date().toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div style={{
                  borderTop: '2px solid #000',
                  paddingTop: '16px',
                  textAlign: 'center',
                  marginBottom: '32px'
                }}>
                  <h1 style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    margin: 0,
                    letterSpacing: '1px'
                  }}>
                    TERM SHEET
                  </h1>
                </div>
              </div>

              {/* SUB-LEASE DETAILS */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px'
                }}>
                  SUB-LEASE DETAILS
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>Sub-Lessee:</td>
                      <td style={{ padding: '8px 0', width: '70%' }}>{selectedTermSheet.subLesseeName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Company Name:</td>
                      <td style={{ padding: '8px 0', textAlign: 'center' }}>{selectedTermSheet.companyName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Registered Name:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.registeredName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Sub-Lessor:</td>
                      <td style={{ padding: '8px 0' }}>Spatium Offices Pvt Ltd</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Building Name:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.buildingName || 'Spatium Commercio'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Location:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.address || 'Spatium Commercio, Office Tower A, 5th Floor, Co-working zone, Mantri Commercio Tech Park, Kariyammana Agrahara Village, Devarabeesanahalli, Bellandur, Bangalore - 560103'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Term Sheet Date:</td>
                      <td style={{ padding: '8px 0' }}>
                        {selectedTermSheet.termSheetDate ? new Date(selectedTermSheet.termSheetDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Term Sheet Number:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.termSheetNumber || 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* LEASE TERMS */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px'
                }}>
                  LEASE TERMS
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>Rentable Seat:</td>
                      <td style={{ padding: '8px 0', width: '70%' }}>{selectedTermSheet.rentableSeats} No of Workstation (Dedicated Desks)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Type of Facility:</td>
                      <td style={{ padding: '8px 0' }}>Co-working space having {selectedTermSheet.rentableSeats}No's dedicated cabin</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Monthly Rent:</td>
                      <td style={{ padding: '8px 0' }}>
                        Total: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedTermSheet.monthlyRent || 0)}/month + Applicable Taxes
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Lock-in Period:</td>
                      <td style={{ padding: '8px 0' }}>
                        Lock-in period is for {selectedTermSheet.lockInPeriod} Months from the day of occupation. In the event Sub-Lessee terminates the lease within Lock-In-Period, in such event the Sub-Lessee shall pay rent for the remaining period of Lock-In.
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Security Deposit:</td>
                      <td style={{ padding: '8px 0' }}>
                        Total: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedTermSheet.securityDeposit || 0)} (Three Months equivalent rent which is equal to {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedTermSheet.securityDeposit || 0)} at the time of signing of the term sheet.)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Escalation:</td>
                      <td style={{ padding: '8px 0' }}>
                        The Sub-Lessee shall pay the Escalation on Rentable Seats, and Parking @ {selectedTermSheet.escalationPercentage}% P.A after the completion of 12 months from the Lease Commencement date.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* PARKING & ADDITIONAL TERMS */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px'
                }}>
                  PARKING & ADDITIONAL TERMS
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>Parking:</td>
                      <td style={{ padding: '8px 0', width: '70%' }}>{selectedTermSheet.parking || 0} spaces provided</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Lease Commencement Date:</td>
                      <td style={{ padding: '8px 0' }}>
                        {selectedTermSheet.rentStartDate ? new Date(selectedTermSheet.rentStartDate).toLocaleDateString('en-GB') : 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Handover Date:</td>
                      <td style={{ padding: '8px 0' }}>
                        {selectedTermSheet.handoverDate ? new Date(selectedTermSheet.handoverDate).toLocaleDateString('en-GB') : 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Notice Period:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.noticePeriod} Days included in the lock-in period</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Sub-Lease Term:</td>
                      <td style={{ padding: '8px 0' }}>{selectedTermSheet.subLeaseTerm || 12} (Twelve Months) months from Lease Commencement Date</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontWeight: 'bold', verticalAlign: 'top' }}>Status:</td>
                      <td style={{ padding: '8px 0' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          backgroundColor: selectedTermSheet.status === 'active' ? '#dcfce7' :
                            selectedTermSheet.status === 'draft' ? '#fef3c7' :
                              selectedTermSheet.status === 'completed' ? '#dbeafe' : '#f3f4f6',
                          color: selectedTermSheet.status === 'active' ? '#166534' :
                            selectedTermSheet.status === 'draft' ? '#92400e' :
                              selectedTermSheet.status === 'completed' ? '#1e40af' : '#374151'
                        }}>
                          {selectedTermSheet.status}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REPRESENTATIVE DETAILS */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px'
                }}>
                  REPRESENTATIVE DETAILS
                </h3>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '18pt 0', margin: '18pt 6pt 0 6pt' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '50%', verticalAlign: 'top' }}>
                        <div style={{ textAlign: 'center', border: '1px solid #cfd3da', padding: '12pt', minHeight: '135pt', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                          <div style={{ fontWeight: '700', fontSize: '11px', margin: '0 0 4pt 0' }}>Yours faithfully,<br />Sub-Lessor</div>
                          {appConfig?.termSheetSignatureUrl ? (
                            <img src={appConfig.termSheetSignatureUrl} alt="Signature" style={{ maxWidth: 95, maxHeight: 48, display: 'block', margin: '6pt auto' }} />
                          ) : null}
                          <div style={{ borderTop: '1px solid #777', marginTop: '16pt', paddingTop: '5pt', fontSize: '6px', color: '#444' }}></div>
                          <div style={{ fontSize: '7px', marginTop: '4pt' }}>
                            <p><em>For and on behalf of</em></p>
                            <p><strong>{selectedTermSheet.subLessorRepresentative?.company || 'Spatium Offices Pvt. Ltd.'}</strong></p>
                            <p>{selectedTermSheet.subLessorRepresentative?.designation || 'CEO'} - {selectedTermSheet.subLessorRepresentative?.name || 'Mr. K V Sathyanarayanaraju'}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ width: '50%', verticalAlign: 'top' }}>
                        <div style={{ textAlign: 'center', border: '1px solid #cfd3da', padding: '12pt', minHeight: '135pt', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                          <div style={{ fontWeight: '700', fontSize: '11px', margin: '0 0 10pt 0' }}>Sub-Lessee</div>
                          <div style={{ borderTop: '1px solid #777', marginTop: '16pt', paddingTop: '5pt', fontSize: '6px', color: '#444' }}></div>
                          <div style={{ fontSize: '7px', marginTop: '4pt' }}>
                            <p><em>For and on behalf of</em></p>
                            <p><strong>{selectedTermSheet.registeredName}</strong></p>
                            <p>{selectedTermSheet.subLesseeRepresentative?.designation || 'Authorized Representative'} - {selectedTermSheet.subLesseeRepresentative?.name || selectedTermSheet.subLesseeName || ''}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ADDITIONAL TERMS */}
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px'
                }}>
                  ADDITIONAL TERMS
                </h3>
                {termEdits && termEdits.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    {termEdits.map((term, index) => (
                      <div key={index} style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <Input
                            placeholder="Title"
                            value={term.title}
                            onChange={e => setTermEdits(prev => prev.map((t, i) => i === index ? { ...t, title: e.target.value } : t))}
                          />
                          <button
                            className="btn btn-danger"
                            onClick={() => setTermEdits(prev => prev.filter((_, i) => i !== index))}
                            style={{ borderRadius: 6 }}
                          >
                            Delete
                          </button>
                        </div>
                        <Input.TextArea
                          placeholder="Content"
                          autoSize={{ minRows: 2 }}
                          value={term.content}
                          onChange={e => setTermEdits(prev => prev.map((t, i) => i === index ? { ...t, content: e.target.value } : t))}
                        />
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={async () => {
                          try {
                            const updated = await updateTermSheet(selectedTermSheet._id, { additionalTerms: termEdits });
                            setSelectedTermSheet(updated);
                          } catch (err) {
                            alert(err.message || 'Failed to save terms');
                          }
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        Save Terms
                      </button>
                    </div>
                  </div>
                )}

                {/* Add new additional term */}
                {selectedTermSheet && (
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 16,
                    background: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Input
                        placeholder="Term title"
                        value={newAdditionalTerm.title}
                        onChange={e => setNewAdditionalTerm({ ...newAdditionalTerm, title: e.target.value })}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Input.TextArea
                        placeholder="Term content"
                        autoSize={{ minRows: 3 }}
                        value={newAdditionalTerm.content}
                        onChange={e => setNewAdditionalTerm({ ...newAdditionalTerm, content: e.target.value })}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setNewAdditionalTerm({ title: '', content: '' })}
                        style={{ borderRadius: 6 }}
                      >
                        Clear
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          if (!newAdditionalTerm.title || !newAdditionalTerm.content) {
                            alert('Please enter both title and content.');
                            return;
                          }
                          try {
                            const updated = await updateTermSheet(selectedTermSheet._id, {
                              additionalTerms: [
                                ...(termEdits || []),
                                { title: newAdditionalTerm.title, content: newAdditionalTerm.content }
                              ]
                            });
                            setSelectedTermSheet(updated);
                            setTermEdits(updated.additionalTerms || []);
                            setNewAdditionalTerm({ title: '', content: '' });
                          } catch (err) {
                            alert(err.message || 'Failed to add term');
                          }
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        Add Term
                      </button>
                    </div>
                  </div>
                )}

                {/* Facility Layout Image Section */}
                {selectedTermSheet && selectedTermSheet.facilityId && selectedTermSheet.facilityId.layoutImage && (
                  <div style={{ marginTop: '32px' }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      marginBottom: '16px',
                      borderBottom: '2px solid #000',
                      paddingBottom: '8px'
                    }}>
                      ANNEXURE
                    </h3>
                    <div style={{
                      textAlign: 'center',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      background: '#fafafa'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginBottom: '12px',
                        color: '#374151'
                      }}>
                        Facility Layout - {selectedTermSheet.facilityId.facilityName}
                      </h4>
                      <img
                        src={`${getApiBaseUrl()}${selectedTermSheet.facilityId.layoutImage}`}
                        alt="Facility Layout"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        onError={(e) => {
                          console.error('Failed to load facility layout image:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                      {selectedTermSheet.facilityId.building && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          {selectedTermSheet.facilityId.building}
                          {selectedTermSheet.facilityId.tower && `, ${selectedTermSheet.facilityId.tower}`}
                          {selectedTermSheet.facilityId.floor && `, Floor ${selectedTermSheet.facilityId.floor}`}
                          {selectedTermSheet.facilityId.wing && `, ${selectedTermSheet.facilityId.wing}`}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leads;


